/* Heat Pump Finder — 4-step lead-gen wizard.
 * Lead is captured at step 3 by posting straight to our own backend (no Shopify
 * contact form, no page reload) — that keeps the office to a SINGLE notification
 * per journey instead of the Shopify auto-emails that duplicated every lead. Steps
 * advance client-side; sessionStorage only preserves state across an in-session
 * refresh. The backend (/api/leads + /api/quotes) is the single source of truth.
 */
document.addEventListener('DOMContentLoaded', function () {
  var root = document.getElementById('hpf');
  if (!root) return;

  var STORAGE_KEY = 'hpf-state-v1';
  var API_URL = (root.getAttribute('data-api-url') || '').replace(/\/+$/, '');
  // Non-blocking backend beacon — persists the quote/event/booking to the
  // unified API (in addition to the Shopify form lead). Never blocks the UI.
  // text/plain keeps it a CORS simple request (no preflight): Chrome refuses
  // keepalive fetches that need a preflight, which silently dropped the
  // booking/event beacons fired during the step-4 page navigation.
  function backend(path, body, opts) {
    if (!API_URL) return Promise.resolve(null);
    return fetch(API_URL + path, Object.assign({
      method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body)
    }, opts || {})).then(function (r) { return r.ok ? r.json() : null; }).catch(function (err) {
      try { console.warn('AVS beacon failed:', path, err); } catch (e) { /* noop */ }
      return null;
    });
  }
  // Google Ads / UTM attribution — the lead-beacon persists it to localStorage on every
  // page (avs_attr); merge the current URL too in case the finder is the landing page. We
  // read it directly now that the finder posts straight to the backend (no beacon relay).
  function attribution() {
    var out = {};
    try { out = JSON.parse(localStorage.getItem('avs_attr') || '{}') || {}; } catch (e) { out = {}; }
    try {
      var p = new URLSearchParams(location.search);
      ['gclid', 'utm_source', 'utm_medium', 'utm_campaign'].forEach(function (k) {
        var v = p.get(k); if (v) out[k] = v;
      });
    } catch (e) { /* noop */ }
    return out;
  }
  // Human-readable one-liner the office reads in the notification email + lead log.
  function detailsLine() {
    return state.roomLabel + ' | ' + (INSULATION_LABELS[state.insulation] || state.insulation) +
      ' | ' + state.suburb + ' | needs ~' + requiredKw().toFixed(1) + ' kW';
  }

  // W per m³ of room volume, by insulation level (2.4 m ceiling assumed)
  var WATTAGE_FACTORS = { newBuild: 45, fullyInsulated: 50, partiallyInsulated: 55, poorlyInsulated: 60 };
  var INSULATION_LABELS = {
    newBuild: 'New build (2016+)',
    fullyInsulated: 'Fully insulated',
    partiallyInsulated: 'Partially insulated',
    poorlyInsulated: 'Poorly insulated'
  };

  var state = loadState() || { stage: 'form', area: null, roomLabel: '', insulation: '', suburb: '', wifi: '', name: '', email: '', phone: '', consent: false };

  function loadState() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
  }
  function saveState() {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }
  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    for (var k in params) payload[k] = params[k];
    window.dataLayer.push(payload);
  }

  // ---- Step navigation -------------------------------------------------
  var booted = false;
  function showStep(step) {
    root.querySelectorAll('.hpf__step').forEach(function (el) {
      el.hidden = el.getAttribute('data-step') !== String(step);
    });
    root.querySelectorAll('.hpf__progress-step').forEach(function (el) {
      var n = parseInt(el.getAttribute('data-progress'), 10);
      // 'thanks' marks every step done; other non-numeric panels treat step 4 as current
      var current = typeof step === 'number' ? step : (step === 'thanks' ? 5 : 4);
      el.classList.toggle('is-active', n === current);
      el.classList.toggle('is-done', n < current);
    });
    root.querySelector('.hpf__progress').hidden = (step === 'posted-fallback');
    // Step 4 (Recommendation) widens the container so 3 product cards breathe.
    root.classList.toggle('is-results', step === 4);
    // Premium step-enter animation (skip step 4 — its results reveal is the beat)
    var vis = root.querySelector('.hpf__step:not([hidden])');
    if (vis && step !== 4) { vis.classList.remove('hpf__step--enter'); void vis.offsetWidth; vis.classList.add('hpf__step--enter'); }
    if (booted) {
      // Scroll the wizard back into view on step change, clearing the
      // sticky header (~120px). Skipped on initial load.
      var top = root.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
    if (typeof step === 'number') {
      track('heat_pump_finder_step', { finder_step: step });
    }
  }

  root.querySelectorAll('.hpf__next').forEach(function (btn) {
    btn.addEventListener('click', function () { showStep(parseInt(btn.getAttribute('data-next'), 10)); });
  });
  root.querySelectorAll('.hpf__back').forEach(function (btn) {
    btn.addEventListener('click', function () { showStep(parseInt(btn.getAttribute('data-back'), 10)); });
  });

  // ---- Step 1: room size + insulation -----------------------------------
  var tiles = root.querySelectorAll('.hpf__tile');
  var customToggle = document.getElementById('hpfCustomSizeToggle');
  var customWrap = document.getElementById('hpfCustomSize');
  var areaInput = document.getElementById('hpfAreaInput');
  var insulationSelect = document.getElementById('hpfInsulation');
  var step1Next = root.querySelector('.hpf__step[data-step="1"] .hpf__next');

  function pulse(el) {
    el.classList.remove('hpf__pulse');
    void el.offsetWidth;
    el.classList.add('hpf__pulse');
    if (navigator.vibrate) { try { navigator.vibrate(8); } catch (e) {} }
  }

  tiles.forEach(function (tile) {
    tile.addEventListener('click', function () {
      tiles.forEach(function (t) { t.classList.remove('is-selected'); });
      tile.classList.add('is-selected');
      pulse(tile);
      state.area = parseFloat(tile.getAttribute('data-area'));
      state.roomLabel = tile.getAttribute('data-room-label');
      customWrap.hidden = true;
      areaInput.value = '';
      validateStep1();
      track('heat_pump_finder_step', { finder_action: 'room_selected', room_type: state.roomLabel });
    });
  });

  customToggle.addEventListener('click', function () {
    customWrap.hidden = !customWrap.hidden;
    if (!customWrap.hidden) areaInput.focus();
  });

  areaInput.addEventListener('input', function () {
    var v = parseFloat(areaInput.value);
    if (v >= 4 && v <= 120) {
      tiles.forEach(function (t) { t.classList.remove('is-selected'); });
      state.area = v;
      state.roomLabel = v + ' m2 (entered)';
    } else if (!areaInput.value) {
      state.area = null;
    }
    validateStep1();
  });

  insulationSelect.addEventListener('change', function () {
    state.insulation = insulationSelect.value;
    validateStep1();
    track('heat_pump_finder_step', { finder_action: 'insulation_selected', insulation_type: state.insulation });
  });

  function validateStep1() {
    step1Next.disabled = !(state.area && state.insulation);
  }

  // ---- Step 2: suburb + Wi-Fi preference ---------------------------------
  var suburbInput = document.getElementById('hpfSuburb');
  var step2Next = root.querySelector('.hpf__step[data-step="2"] .hpf__next');
  suburbInput.addEventListener('input', function () {
    state.suburb = suburbInput.value.trim();
    step2Next.disabled = state.suburb.length < 2;
  });

  // Wi-Fi preference is a SOFT signal (never gates Next, never hard-filters
  // recommendations) — it only nudges Wi-Fi-capable models up the list.
  var wifiBtns = root.querySelectorAll('.hpf__step[data-step="2"] .hpf__choice-btn');
  wifiBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      wifiBtns.forEach(function (b) { b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false'); });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      pulse(btn);
      state.wifi = btn.getAttribute('data-wifi'); // 'yes' | 'no'
      track('heat_pump_finder_step', { finder_action: 'wifi_preference', wifi_preference: state.wifi });
    });
  });

  // ---- Step 3: contact + lead submit --------------------------------------
  var leadForm = document.getElementById('FinderLeadForm');
  var nameInput = document.getElementById('hpfName');
  var emailInput = document.getElementById('hpfEmail');
  var phoneInput = document.getElementById('hpfPhone');
  var submitBtn = document.getElementById('hpfSeeRecommendation');

  function validStep3() {
    var emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value.trim());
    var phoneOk = phoneInput.value.replace(/[^\d]/g, '').length >= 7;
    return nameInput.value.trim().length >= 2 && emailOk && phoneOk;
  }
  [nameInput, emailInput, phoneInput].forEach(function (el) {
    el.addEventListener('input', function () { submitBtn.disabled = !validStep3(); });
  });

  function requiredKw() {
    var factor = WATTAGE_FACTORS[state.insulation] || 50;
    return (state.area * 2.4 * factor) / 1000;
  }

  leadForm.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!validStep3()) return;

    state.name = nameInput.value.trim();
    state.email = emailInput.value.trim();
    state.phone = phoneInput.value.trim();
    // Implied consent — the form promises the recommendation + follow-ups, and
    // leads are captured to our own backend (no Shopify marketing opt-in needed).
    state.consent = true;
    saveState();

    var kw = requiredKw();
    var details = detailsLine();
    var attr = attribution();

    submitBtn.disabled = true;
    submitBtn.textContent = 'One moment…';

    track('generate_lead', {
      finder_action: 'lead_captured',
      room_type: state.roomLabel,
      insulation_type: state.insulation,
      suburb: state.suburb,
      required_kw: kw.toFixed(1),
      marketing_consent: state.consent
    });

    // Full follow-up log + the customer's recommendation email. The OFFICE notification is
    // NOT sent from here — the backend drives it off the quote (below) so the office gets
    // exactly one email per journey (the booking one if they book, else a short-debounce sweep).
    backend('/api/leads', Object.assign({
      name: state.name, email: state.email, phone: state.phone, suburb: state.suburb,
      source: 'Heat Pump Finder', flow_type: 'finder', tags: 'finder-lead',
      summary: details, page_url: location.href
    }, attr));

    // Quote record — its quoteId ties the booking request and the office notification
    // together (shared dedup key). Reveal the recommendation once we have it, so a fast
    // booker can never out-race the quoteId; a 3s safety net reveals regardless.
    var revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      state.stage = 'results';
      saveState();
      renderResults();
      showStep(4);
    }
    backend('/api/quotes', Object.assign({
      flow_type: 'finder', intent: 'recommendation', summary: details,
      name: state.name, email: state.email, phone: state.phone, suburb: state.suburb,
      payload: { area: state.area, insulation: state.insulation, requiredKw: kw, consent: state.consent }
    }, attr)).then(function (r) {
      if (r && r.quoteId) { state.quoteId = r.quoteId; saveState(); }
      backend('/api/events', { surface: 'finder', event_name: 'generate_lead', quote_id: state.quoteId || null });
      reveal();
    });
    setTimeout(reveal, 3000);
  });

  // ---- Step 4: recommendation --------------------------------------------
  // Variant titles look like "2.5kW Cooling / 3.2kW Heating" — size on the
  // HEATING figure (NZ winter intent), falling back to the first kW.
  function parseKw(str) {
    if (!str) return 0;
    var heat = String(str).match(/(\d+(?:\.\d+)?)\s*kw\s*heating/i);
    if (heat) return parseFloat(heat[1]);
    var m = String(str).match(/(\d+(?:\.\d+)?)\s*kw/i);
    return m ? parseFloat(m[1]) : 0;
  }

  function pickVariant(variants, kw) {
    var sized = variants
      .map(function (v) { return { v: v, kw: parseKw(v.option1 || v.title) }; })
      .filter(function (x) { return x.kw > 0; });
    if (!sized.length) return null;
    // Prefer the smallest unit that meets or exceeds the requirement —
    // never undersize a heat pump.
    var over = sized.filter(function (x) { return x.kw >= kw; })
      .sort(function (a, b) { return a.kw - b.kw; });
    if (over.length) return over[0];
    return sized.sort(function (a, b) { return b.kw - a.kw; })[0];
  }

  // Wi-Fi capability detection — same signal the benefit chips use, so the
  // sort and the displayed "Built-in Wi-Fi control" benefit always agree.
  function hasWifi(p) {
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.indexOf('wifi') !== -1) return true;
    var text = (String(p.title || '') + ' ' + String(p.description || '')).toLowerCase();
    return /wi-?fi/.test(text);
  }

  function buildRecommendations(kw) {
    var wantWifi = state.wifi === 'yes';
    var products = (window.hpfProducts || [])
      .map(function (p) {
        var pick = pickVariant(p.variants || [], kw);
        if (!pick) return null;
        var score = pick.kw >= kw ? pick.kw - kw : (kw - pick.kw) * 3;
        return { product: p, variant: pick.v, kw: pick.kw, score: score, wifi: hasWifi(p), brand: (p.vendor || p.title.split(' ')[0]) };
      })
      .filter(Boolean)
      // Primary key: sizing fit (smallest over-spec wins). Secondary key (only
      // when the user asked for Wi-Fi): float Wi-Fi-capable models up. This is
      // a SOFT preference — every model still stays in the list, so a thin
      // Wi-Fi match can never zero out the recommendations.
      .sort(function (a, b) {
        if (wantWifi && a.wifi !== b.wifi) return a.wifi ? -1 : 1;
        return a.score - b.score;
      });

    var picked = [];
    var brands = {};
    products.forEach(function (r) {
      if (picked.length >= 3 || brands[r.brand]) return;
      brands[r.brand] = true;
      picked.push(r);
    });
    products.forEach(function (r) {
      if (picked.length >= 3 || picked.indexOf(r) !== -1) return;
      picked.push(r);
    });
    return picked;
  }

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  var BRAND_LINES = {
    'Daikin': 'Trusted Daikin reliability',
    'Mitsubishi Electric': 'Mitsubishi Electric — built to last',
    'Mitsubishi': 'Mitsubishi Electric — built to last',
    'Panasonic': 'Panasonic clean-air technology'
  };

  function roomShort() {
    return (state.roomLabel || 'room').replace(/\s*\(.*\)\s*/, '').toLowerCase();
  }

  // Universal AVS value — true for every install, used to top up thin cards
  // so each recommendation carries a full set of benefits.
  var UNIVERSAL_BENEFITS = [
    'Expert AVS installation included',
    'Backed by manufacturer + 5-yr workmanship warranty'
  ];

  // Benefit chips derived from Shopify data (description + tags) — no extra
  // content authoring needed. A curated metafield can override this later.
  function deriveBenefits(r) {
    var out = ['Right-sized for your ' + roomShort()];

    var desc = String(r.product.description || '');
    var text = desc.toLowerCase();
    var tags = (r.product.tags || []).map(function (t) { return String(t).toLowerCase(); });

    if (tags.indexOf('wifi') !== -1 || /wi-?fi/.test(text)) out.push('Built-in Wi-Fi control');

    var db = desc.match(/(\d{2})\s*db/i);
    if (db) out.push('Whisper-quiet — from ' + db[1] + ' dB');
    else if (/quiet/.test(text)) out.push('Quiet operation');

    if (/ecocore|energy.?efficien|less power|low.{0,4}running/.test(text)) out.push('Energy efficient — lower running costs');
    if (/anti-?bacterial|filter|purif|clean air/.test(text)) out.push('Cleaner, filtered air');

    var brandLine = BRAND_LINES[r.brand];
    if (brandLine && out.length < 4) out.push(brandLine);
    // Top up to 4 so every card reads as a full recommendation, not a thin one.
    for (var i = 0; i < UNIVERSAL_BENEFITS.length && out.length < 4; i++) {
      out.push(UNIVERSAL_BENEFITS[i]);
    }
    return out.slice(0, 4);
  }

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Count-up reveal for the hero kW figure
  function rollKw(el, target) {
    if (reduceMotion) { el.textContent = target.toFixed(1); return; }
    var start = null, dur = 780;
    function frame(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / dur);
      var e = 1 - Math.pow(1 - t, 3); // easeOutCubic
      el.textContent = (target * e).toFixed(1);
      if (t < 1) requestAnimationFrame(frame); else el.textContent = target.toFixed(1);
    }
    requestAnimationFrame(frame);
  }

  function renderResults() {
    var kw = requiredKw();
    var recs = buildRecommendations(kw);
    var wrap = document.getElementById('hpfResults');
    var firstName = state.name ? esc(state.name.split(' ')[0]) : '';

    var reveal =
      '<div class="hpf__reveal">' +
        '<p class="hpf__reveal-label">Your room needs about</p>' +
        '<div class="hpf__reveal-kw"><span class="hpf__kw-num" id="hpfKwNum">0.0</span><span class="hpf__kw-unit">kW</span></div>' +
        '<p class="hpf__reveal-sub">heating capacity for your ' + esc(state.roomLabel.toLowerCase()) + ' in ' + esc(state.suburb) + '.' +
        (firstName ? ' Here’s what we’d recommend, ' + firstName + ' —' : ' Here’s what we’d recommend —') +
        ' a copy’s on its way to your inbox.</p>' +
      '</div>';

    if (!recs.length) {
      wrap.innerHTML = reveal +
        '<p class="hpf__result-summary">We’ll match the right unit when we call to confirm your free measure &amp; quote.</p>';
      rollKw(document.getElementById('hpfKwNum'), kw);
      return;
    }

    wrap.innerHTML = reveal + '<div class="hpf__cards">' + recs.map(function (r) {
      var benefits = deriveBenefits(r);
      var bullets = benefits.map(function (b, i) {
        return '<li' + (i === 0 ? ' class="hpf__benefit-lead"' : '') + '>' + esc(b) + '</li>';
      }).join('');
      return (
        '<div class="hpf__card hpf__card--in">' +
          '<div class="hpf__card-imgwrap">' +
            (r.product.image ? '<img class="hpf__card-img" src="' + esc(r.product.image) + '" alt="' + esc(r.product.title) + '" loading="lazy">' : '') +
            (r.brand ? '<span class="hpf__card-brand">' + esc(r.brand) + '</span>' : '') +
          '</div>' +
          '<div class="hpf__card-body">' +
            '<h3 class="hpf__card-title">' + esc(r.product.title) + '</h3>' +
            '<ul class="hpf__benefits">' + bullets + '</ul>' +
            '<p class="hpf__card-price">From ' + esc(r.variant.price) + ' <span>installed · ' + r.kw + ' kW heating</span></p>' +
            '<a class="hpf__card-link" href="' + esc(r.product.url) + '">View details</a>' +
          '</div>' +
        '</div>'
      );
    }).join('') + '</div>';

    rollKw(document.getElementById('hpfKwNum'), kw);
    // Stagger the cards in after the number lands
    wrap.querySelectorAll('.hpf__card--in').forEach(function (c, i) {
      c.style.animationDelay = (reduceMotion ? 0 : 420 + i * 110) + 'ms';
    });
  }

  // ---- Booking form (step 4 → thanks) --------------------------------------
  var bookingForm = document.getElementById('FinderBookingForm');
  var bookingCta = bookingForm.querySelector('.hpf__booking-cta');
  bookingForm.addEventListener('submit', function (e) {
    e.preventDefault();
    state.stage = 'booked';
    saveState();
    var preferred = document.getElementById('hpfPreferredTime').value;
    if (bookingCta) { bookingCta.disabled = true; bookingCta.textContent = 'One moment…'; }
    track('heat_pump_finder_booking_request', { finder_action: 'booking_requested', preferred_time: preferred });
    // Site-meet request → our backend. source:'finder' routes the office notification to the
    // single finder email (carrying the preferred time), keyed to the quote so it and the
    // cron sweep dedupe to exactly one office email per journey.
    backend('/api/bookings', {
      quoteId: state.quoteId || null, type: 'site_meet', source: 'finder',
      name: state.name, email: state.email, phone: state.phone, suburb: state.suburb,
      preferredTime: preferred, summary: detailsLine(),
      idempotencyKey: 'hpf-book-' + (state.quoteId || state.email)
    });
    backend('/api/events', { surface: 'finder', event_name: 'booking_requested', quote_id: state.quoteId || null });
    showStep('thanks');
  });

  document.getElementById('hpfStartOver').addEventListener('click', function () {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
    window.location.href = window.location.pathname;
  });

  // ---- Boot: decide which panel to show ------------------------------------
  // Steps advance client-side now (no Shopify reload), so we only resume state across an
  // in-session refresh: booked → thanks, recommendation shown → step 4, otherwise start fresh.
  if (state.stage === 'booked') {
    showStep('thanks');
  } else if (state.stage === 'results') {
    renderResults();
    showStep(4);
  } else {
    state.stage = 'form';
    saveState();
    showStep(1);
  }
  booted = true;
});
